import React, { Fragment, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useAlert } from "react-alert";
import axios from "axios";
import swal from "sweetalert";

import Loader from "../Loader";
import { loadUser } from "../../actions/authActions";
import AuthCommonBanner from "./AuthCommonBanner";

const ForceChangePassword = ({ history }) => {
  const alert = useAlert();
  const dispatch = useDispatch();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorData, setErrorData] = useState({});
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { user, isAuthenticated } = useSelector((state) => state.auth);

  useEffect(() => {
    // If user doesn't need to change password, redirect to dashboard
    if (isAuthenticated && user && !user.mustChangePassword) {
      history.push("/dashboard");
    }
  }, [user, isAuthenticated, history]);

  const submitHandler = async (e) => {
    e.preventDefault();
    setErrorData({});

    // Validate
    if (!newPassword) {
      setErrorData({ newPassword: "New password is required" });
      return;
    }

    if (newPassword.length < 6) {
      setErrorData({ newPassword: "Password must be at least 6 characters" });
      return;
    }

    if (!confirmPassword) {
      setErrorData({ confirmPassword: "Please confirm your password" });
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorData({ confirmPassword: "Passwords do not match" });
      return;
    }

    setLoading(true);

    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      };

      const { data } = await axios.post(
        `/force-password-change`,
        { newPassword, confirmPassword },
        config
      );

      if (data.success) {
        // Save new token if returned
        if (data.token) {
          localStorage.setItem("token", data.token);
        }
        // Reload user data to clear mustChangePassword flag and auto-login
        await dispatch(loadUser());
        // Redirect to dashboard immediately
        history.push("/dashboard");
      }
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to update password";
      swal("Error", message, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      {loading ? (
        <Loader />
      ) : (
        <Fragment>
          <section className="registration-page clearfix fullHt">
            <div className="right-regi-step login-page">
              <div className="vh-center">
                <div className="log-logo">
                  <Link to="/">
                    <img src="/assets/img/logo.png" alt="SITE-LOGO" />
                  </Link>
                </div>
                <h1>
                  Update Your <span>Password</span>
                </h1>
                <p className="text-center" style={{ color: "#666", marginBottom: "20px" }}>
                  For your security, please create a new password to continue
                </p>
                <div className="inner-que">
                  <form onSubmit={submitHandler}>
                    <div className="row">
                      <div className="col-sm-12">
                        <div className="form-group">
                          <label htmlFor="newPassword">
                            New Password<span className="sub">*</span>
                          </label>
                          <div style={{ position: "relative" }}>
                            <input
                              type={showNewPassword ? "text" : "password"}
                              className="form-control"
                              id="newPassword"
                              placeholder="Enter New Password"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              tabIndex="1"
                              style={{ paddingRight: "40px" }}
                            />
                            <i
                              className={`fa ${showNewPassword ? "fa-eye-slash" : "fa-eye"}`}
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              style={{
                                position: "absolute",
                                right: "12px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                cursor: "pointer",
                                color: "#666"
                              }}
                            />
                          </div>
                          {errorData.newPassword && (
                            <span className="text-danger">
                              {errorData.newPassword}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-sm-12">
                        <div className="form-group">
                          <label htmlFor="confirmPassword">
                            Confirm Password<span className="sub">*</span>
                          </label>
                          <div style={{ position: "relative" }}>
                            <input
                              type={showConfirmPassword ? "text" : "password"}
                              className="form-control"
                              id="confirmPassword"
                              placeholder="Confirm New Password"
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              tabIndex="2"
                              style={{ paddingRight: "40px" }}
                            />
                            <i
                              className={`fa ${showConfirmPassword ? "fa-eye-slash" : "fa-eye"}`}
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              style={{
                                position: "absolute",
                                right: "12px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                cursor: "pointer",
                                color: "#666"
                              }}
                            />
                          </div>
                          {errorData.confirmPassword && (
                            <span className="text-danger">
                              {errorData.confirmPassword}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div
                      className="alert alert-warning"
                      style={{ marginTop: "15px", marginBottom: "20px" }}
                    >
                      <strong>Password Requirements:</strong>
                      <ul style={{ marginBottom: 0, paddingLeft: "20px", marginTop: "5px" }}>
                        <li>At least 6 characters long</li>
                        <li>Choose something you can remember</li>
                        <li>Don't reuse your temporary password</li>
                      </ul>
                    </div>

                    <ul className="log-btn-main clearfix">
                      <li style={{ width: "100%" }}>
                        <button
                          type="submit"
                          className="btn btn-primary"
                          style={{ width: "100%" }}
                          disabled={loading}
                          tabIndex="3"
                        >
                          {loading ? "Updating..." : "Update Password"}
                        </button>
                      </li>
                    </ul>
                  </form>
                </div>
              </div>
            </div>
            <AuthCommonBanner></AuthCommonBanner>
          </section>
        </Fragment>
      )}
    </Fragment>
  );
};

export default ForceChangePassword;
